<template>
  <div class="create-amenity">
    <SidebarLayout3 :breadcrumb="breadcrumb" :pageTitle="pageTitle">
      <!-- Sidebar -->
      <template #sidebar>
        <Heading element="h2" class="sidebar-title">
          Ações da página
        </Heading>
        <div class="sidebar-buttons">
          <!-- Botão salvar só fica ativado quando o nome for preenchido -->
          <Button
            :disabled="disabledSave"
            :tooltipMinWidth="150"
            tooltipText="Insira um nome para salvar"
            @click="saveAmenity()"
          >
            Salvar
          </Button>
          <!-- Se a amenidade estiver ativa -->
          <Button
            v-show="showDeactivateButton"
            variation="error"
            @click="showModal = true"
          >
            Desativar amenidade
          </Button>
          <!-- Se a amenidade estiver inativa -->
          <Button v-show="showReactivateButton" @click="reactivateAmenity()">
            Reativar
          </Button>
          <!-- Botão retornar -->
          <Button
            layout="outline"
            @click="$router.push({ path: '/amenidades' })"
          >
            Voltar sem salvar
          </Button>
        </div>
      </template>

      <!-- Conteúdo -->
      <template #content>
        <!-- Abas para inserir o nome da amenidade -->
        <Tabs>
          <template
            v-for="(tab, index) in tabs"
            v-slot:[`tab-title-${index}`]="{ title, tabObj }"
          >
            <span :key="tab.title" data-cy="tab-title" class="tab-title">
              <img :src="tabObj.extraData.flag" />
              {{ title }}
            </span>
          </template>
          <Tab
            v-for="tab in tabs"
            :extraData="tab"
            :key="tab.title"
            :title="tab.title"
          >
            <div class="name-input">
              <!-- Input de nome -->
              <FormInput
                v-model="nameInput[tab.dataAttribute].value"
                :label="'Nome (' + tab.label + ')'"
                :maxlength="50"
                :required="tab.dataAttribute === 'pt'"
                :title="tab.title"
                data-cy="input-name"
                placeholder="Insira o nome da amenidade"
              />
              <!-- Contador de caracteres -->
              <Paragraph class="character-count">
                {{ nameInput[tab.dataAttribute].value.length }}/50 caracteres
              </Paragraph>
            </div>
          </Tab>
        </Tabs>

        <!-- Apenas para edição -->
        <!-- Botão para reativar (só fica disponível se a amenidade estiver inativa) -->
        <div v-if="showReactivateButton" class="inactive">
          <Paragraph>
            Essa amenidade está inativa <br />
            <button class="inactive--button" @click="reactivateAmenity()">
              Clique aqui para reativar.
            </button>
          </Paragraph>
        </div>
        <!-- / Apenas para edição -->

        <SeparateLine :right="0" :left="0" />

        <!-- Botões no rodapé da página -->
        <span class="bottom-action">
          <Button
            layout="outline"
            class="bottom-action--button"
            @click="$router.push({ path: '/amenidades' })"
          >
            Voltar sem salvar
          </Button>
          <!-- Botão salvar só fica ativo quando o nome for preenchido -->
          <Button
            :disabled="disabledSave"
            popperStrategy="absolute"
            tooltipText="Insira um nome para salvar"
            class="bottom-action--button"
            data-cy="save-amenity"
            @click="saveAmenity()"
          >
            Salvar
          </Button>
        </span>
      </template>
    </SidebarLayout3>
    <!-- Modal de deleção -->
    <AlertModal
      v-model="showModal"
      :buttonClose="true"
      title="Desativar amenidade"
      @alert-modal-confirm="inactivateAmenity()"
    >
      <template #text>
        Ao desativar essa amenidade, ela não será <br />
        mais listada nos hotéis/quartos que a utilizem. <br />
        Tem certeza de que quer fazer isso?
      </template>
    </AlertModal>
  </div>
</template>

<script>
/** Bandeiras de idiomas */
import en from '@/assets/images/en-flag.png'
import es from '@/assets/images/es-flag.png'
import pt from '@/assets/images/ptbr-flag.png'

/** Componentes */
import AlertModal from '@solucoes/components-lib-tmp/src/components/patterns/AlertModal'
import Button from '@solucoes/components-lib-tmp/src/components/elements/Button'
import FormInput from '@solucoes/components-lib-tmp/src/components/elements/FormInput'
import Heading from '@solucoes/components-lib-tmp/src/components/elements/Heading'
import Paragraph from '@solucoes/components-lib-tmp/src/components/elements/Paragraph'
import SeparateLine from '@solucoes/components-lib-tmp/src/components/elements/SeparateLine'
import SidebarLayout3 from '@/layouts/SidebarLayout3'
import Tab from '@/components/_base/patterns/Tab'
import Tabs from '@/components/_base/patterns/Tabs'

export default {
  name: 'CreateAmenity',
  components: {
    AlertModal,
    Button,
    FormInput,
    Heading,
    Paragraph,
    SeparateLine,
    SidebarLayout3,
    Tab,
    Tabs
  },
  data() {
    return {
      /** Caminho de pão */
      breadcrumb: [
        {
          label: 'Configurações'
        },
        {
          label: 'Amenidades',
          url: '/amenidades',
          isSpa: true
        },
        {
          label: 'Criar nova amenidade'
        }
      ],
      /** Input do nome */
      nameInput: { pt: { value: '' }, en: { value: '' }, es: { value: '' } },
      /** Título da página */
      pageTitle: '',
      /** Exibição do botão de desativar */
      showDeactivateButton: false,
      /** Visualização da modal */
      showModal: false,
      /** Exibição do botão de reativar */
      showReactivateButton: false,
      /** Status da amenidade */
      status: true,
      /** Abas com os idiomas */
      tabs: [
        {
          title: 'PT',
          label: 'Português',
          flag: pt,
          dataAttribute: 'pt'
        },
        {
          title: 'EN',
          label: 'English',
          flag: en,
          dataAttribute: 'en'
        },
        {
          title: 'ES',
          label: 'Español',
          flag: es,
          dataAttribute: 'es'
        }
      ]
    }
  },
  computed: {
    disabledSave() {
      /** Habilitação do botão de salvar */
      if (this.nameInput.pt.value.trim().length < 3) {
        return true
      }
      return false
    }
  },

  async created() {
    if (this.$route.params.id) {
      await this.getOneAmenity()
    } else {
      this.pageTitle = 'Criar nova amenidade'
    }
  },
  methods: {
    getOneAmenity() {
      /** Retorna os dados da amenidade */
      this.$amenities.getAmenities(this.$route.params.id).then((response) => {
        const data = response.data
        data.idiomas.map((item) => {
          if (item.idioma === 'pt-BR' && item.nome !== null) {
            this.nameInput.pt.value = item.nome
          } else if (item.idioma === 'en-US' && item.nome !== null) {
            this.nameInput.en.value = item.nome
          } else if (item.idioma === 'es-ES' && item.nome !== null) {
            this.nameInput.es.value = item.nome
          }
        })

        this.pageTitle = 'Editando ' + this.nameInput.pt.value
        this.breadcrumb[2].label = this.pageTitle
        this.status = data.ativo

        if (this.status) {
          this.showDeactivateButton = true
        } else {
          this.showReactivateButton = true
        }
      })
    },
    inactivateAmenity() {
      /** Desativa a amenidade */
      const id = parseInt(this.$route.params.id)

      this.$amenities
        .inactivateAmenities(id)
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Amenidade inativada'
          })

          this.$router.push({ path: '/amenidades' })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Erro ao inativar amenidade'
          })
        })
    },
    reactivateAmenity() {
      /** Reativa a amenidade */
      this.status = true
      this.saveAmenity()
    },
    saveAmenity() {
      /** Salva os dados da amenidade */
      const array = [
        {
          idioma: 'pt-BR',
          nome: this.nameInput.pt.value
        }
      ]

      if (this.nameInput.en.value.trim() !== '') {
        array.push({
          idioma: 'en-US',
          nome: this.nameInput.en.value
        })
      }
      if (this.nameInput.es.value.trim() !== '') {
        array.push({
          idioma: 'es-ES',
          nome: this.nameInput.es.value
        })
      }
      /** Edição */
      if (this.$route.params.id) {
        this.$amenities
          .updateAmenity({
            codigo: parseInt(this.$route.params.id),
            ativo: this.status,
            idiomas: [...array]
          })
          .then(() => {
            this.$bus.$emit('show-alert-chip', {
              message: 'Amenidade atualizada'
            })

            this.$router.push({ path: '/amenidades' })
          })
          .catch((error) => {
            this.$bus.$emit('show-alert-chip', {
              message: error.messages[0]
            })
          })
      } else {
        /** Criação */
        this.$amenities
          .createAmenity({
            ativo: true,
            idiomas: [...array]
          })
          .then(() => {
            this.$bus.$emit('show-alert-chip', {
              message: 'Amenidade criada com sucesso'
            })

            this.$router.push({ path: '/amenidades' })
          })
          .catch((error) => {
            this.$bus.$emit('show-alert-chip', {
              message: error.messages[0]
            })
          })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .button--tooltip {
  width: 165px;
}
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
}
::v-deep .tabs--head {
  margin: 0px;
}
::v-deep .tabs--content {
  padding: 37px 0 40px 0;
}
.sidebar {
  &-title {
    text-align: left;
    margin-top: 35px;
    margin-bottom: 15px;
    margin-left: 15px;
  }
  &-buttons {
    display: flex;
    flex-direction: column;
    width: 215px;
    gap: 10px;
    margin: auto;
  }
}
.name-input {
  width: 518px;
  display: flex;
  flex-direction: column;
  .character-count {
    margin-left: auto;
    padding-top: 7px;
    color: $gray-dark;
  }
}
.inactive {
  &--button {
    border: none;
    background: inherit;
    color: $primary;
    line-height: 21px;
    padding: 10px 0 0 0px;
    text-decoration-line: underline;
  }
}
.info-log {
  display: flex;
  gap: 20px;
  color: $gray-dark;
  &--title {
    padding-bottom: 15px;
  }
  &--data {
    line-height: 18px;
    width: 139px;
  }
}
.bottom-action {
  display: flex;
  justify-content: space-between;
  &--button {
    width: 180px;
  }
}
.tab-title {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
